/**
 * The Settings entry point to the household screen.
 *
 * Replaces a two-way branch on `householdId` that showed "Household paired"
 * whenever a household document existed. It existed from the moment the Share
 * screen was opened — before anyone had been invited, let alone joined — so
 * the row claimed you were sharing with someone when you were alone, and gave
 * no way to find out who that someone supposedly was.
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';
import { useHouseholdMembers } from '../../hooks/useHouseholdMembers';

/** Whole hours left, floored, never negative. */
const hoursUntil = (millis: number): number =>
  Math.max(0, Math.floor((millis - Date.now()) / 3_600_000));

export const HouseholdRow = ({ onPress }: { onPress: () => void }) => {
  const { t } = useTranslation();
  const household = useHouseholdMembers();

  const { title, subtitle, leading, trailing } = describe();

  function describe() {
    if (household.status === 'paired') {
      return {
        title: t('household.rowPairedTitle', {
          name: household.partnerName ?? household.partnerEmail ?? '—',
        }),
        subtitle: household.partnerName ? household.partnerEmail : null,
        leading: (
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{household.partnerInitial ?? '?'}</Text>
          </View>
        ),
        trailing: <View style={[styles.dot, styles.dotLive]} />,
      };
    }

    if (household.status === 'pending' && household.pendingInvite) {
      const hours = hoursUntil(household.pendingInvite.expiresAt.toMillis());
      return {
        title: t('household.rowPendingTitle'),
        subtitle: t(
          hours > 0
            ? 'household.rowPendingSubtitle'
            : 'household.rowPendingSubtitleSoon',
          { code: household.pendingInvite.code, hours }
        ),
        leading: (
          <View style={styles.iconWrap}>
            <Feather name="clock" size={18} color={Paprika.accentOrange} />
          </View>
        ),
        trailing: <View style={[styles.dot, styles.dotPending]} />,
      };
    }

    return {
      title: t('household.rowSoloTitle'),
      subtitle: t('household.rowSoloSubtitle'),
      leading: (
        <View style={styles.iconWrap}>
          <Feather name="user-plus" size={18} color={Paprika.accent} />
        </View>
      ),
      trailing: (
        <Feather name="chevron-right" size={20} color={Paprika.textSecondary} />
      ),
    };
  }

  return (
    <TouchableOpacity style={styles.row} onPress={onPress}>
      {leading}
      <View style={styles.text}>
        <Text style={styles.title}>{title}</Text>
        {subtitle ? (
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {trailing}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    padding: 14,
    marginTop: PaprikaSpacing.md,
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: Paprika.surfaceHighest,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: Paprika.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.onPrimary,
  },
  text: { flex: 1, minWidth: 0 },
  title: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
  },
  subtitle: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  dot: { width: 8, height: 8, borderRadius: 4 },
  dotPending: { backgroundColor: Paprika.accentOrange },
  dotLive: { backgroundColor: Paprika.success },
});
